import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  CurrencyEur, Receipt, CheckCircle, ArrowClockwise, WarningCircle, X,
  Users, UsersThree, Brain, Buildings,
} from "@phosphor-icons/react";

const KIND_LABEL = {
  coach: "Work coach",
  coachee: "Coachee",
  breintype_test: "Breintype-test",
};

const KIND_META = [
  { key: "coaches",         apiKind: "coach",          title: "Work coaches",    icon: UsersThree, color: "#1E88E5" },
  { key: "coachees",        apiKind: "coachee",        title: "Coachees",        icon: Users,      color: "#43A047" },
  { key: "breintype_tests", apiKind: "breintype_test", title: "Breintype-tests", icon: Brain,      color: "#E53935" },
];

export default function AdminBillingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // { companyId, companyName, kind }

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/billing/summary");
      setRows(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const totals = useMemo(() => {
    const t = {
      coaches: { total: 0, outstanding: 0, invoiced: 0 },
      coachees: { total: 0, outstanding: 0, invoiced: 0 },
      breintype_tests: { total: 0, outstanding: 0, invoiced: 0 },
    };
    for (const row of rows) {
      for (const meta of KIND_META) {
        const c = row[meta.key];
        if (!c) continue;
        t[meta.key].total += c.total;
        t[meta.key].outstanding += c.outstanding;
        t[meta.key].invoiced += c.invoiced;
      }
    }
    return t;
  }, [rows]);

  return (
    <AppLayout title="Facturatie" breadcrumbs={[{ label: "Admin" }, { label: "Facturatie" }]}>
      <div data-testid="admin-billing-page">
        {/* Header + refresh */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <p className="text-sm text-zinc-600 max-w-2xl">
            Overzicht per bedrijf van alle factureerbare items. Klik op een
            <strong> Nog te factureren</strong>-getal om die items als
            gefactureerd te markeren.
          </p>
          <button
            onClick={fetchList}
            data-testid="billing-refresh"
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" /> Ververs
          </button>
        </div>

        {/* Global totals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {KIND_META.map((m) => (
            <div
              key={m.key}
              className="neuro-card-bold p-5 bg-white"
              data-testid={`billing-total-${m.key}`}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="inline-flex h-10 w-10 items-center justify-center rounded-sm"
                  style={{ background: `${m.color}15`, color: m.color }}
                >
                  <m.icon size={20} weight="duotone" />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{m.title}</div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-3xl font-bold tracking-tight tabular-nums">
                  {totals[m.key].outstanding}
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">nog te factureren</div>
              </div>
              <div className="mt-1 text-xs text-zinc-500 tabular-nums">
                {totals[m.key].invoiced} gefactureerd · {totals[m.key].total} totaal
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500">Laden...</div>
        ) : rows.length === 0 ? (
          <div className="neuro-card p-12 text-center">
            <Receipt size={32} className="mx-auto text-zinc-300" />
            <h3 className="font-display text-lg font-bold mt-3 tracking-tight">Nog geen bedrijven</h3>
          </div>
        ) : (
          <div className="neuro-card-bold bg-white overflow-x-auto" data-testid="billing-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-900">
                  <th className="text-left py-3 px-4 font-display text-xs uppercase tracking-widest">Bedrijf</th>
                  {KIND_META.map((m) => (
                    <th
                      key={m.key}
                      className="text-left py-3 px-4 font-display text-xs uppercase tracking-widest"
                      style={{ color: m.color }}
                    >
                      {m.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.company_id || "orphan"}
                    className="border-b border-zinc-100 hover:bg-zinc-50/50"
                    data-testid={`billing-row-${row.company_id || "orphan"}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Buildings size={16} className={row.company_id ? "text-zinc-500" : "text-zinc-300"} weight="duotone" />
                        <span className={row.company_id ? "font-medium" : "italic text-zinc-500"}>
                          {row.company_name}
                        </span>
                      </div>
                    </td>
                    {KIND_META.map((m) => {
                      const c = row[m.key];
                      const canClick = row.company_id != null && c.outstanding > 0;
                      return (
                        <td key={m.key} className="py-3 px-4">
                          <CountsCell
                            counts={c}
                            color={m.color}
                            onClickOutstanding={
                              canClick
                                ? () => setModal({
                                    companyId: row.company_id,
                                    companyName: row.company_name,
                                    kind: m.apiKind,
                                    kindTitle: m.title,
                                  })
                                : null
                            }
                            testidPrefix={`cell-${row.company_id || "orphan"}-${m.key}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <InvoiceModal
          companyId={modal.companyId}
          companyName={modal.companyName}
          kind={modal.kind}
          kindTitle={modal.kindTitle}
          onClose={() => setModal(null)}
          onMarked={() => { setModal(null); fetchList(); }}
        />
      )}
    </AppLayout>
  );
}

function CountsCell({ counts, color, onClickOutstanding, testidPrefix }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testidPrefix}>
      <div className="flex items-baseline gap-2">
        <div className="tabular-nums font-display text-lg font-bold tracking-tight">
          {counts.total}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">totaal</div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        {onClickOutstanding ? (
          <button
            type="button"
            onClick={onClickOutstanding}
            data-testid={`${testidPrefix}-outstanding-btn`}
            className="tabular-nums font-semibold hover:underline"
            style={{ color }}
            title="Klik om te markeren als gefactureerd"
          >
            {counts.outstanding} <span className="text-[10px] uppercase tracking-widest">nog</span>
          </button>
        ) : (
          <span className="tabular-nums text-zinc-400" data-testid={`${testidPrefix}-outstanding-readonly`}>
            {counts.outstanding} <span className="text-[10px] uppercase tracking-widest">nog</span>
          </span>
        )}
        <span className="tabular-nums text-zinc-500">
          {counts.invoiced} <span className="text-[10px] uppercase tracking-widest">✓</span>
        </span>
      </div>
    </div>
  );
}

function InvoiceModal({ companyId, companyName, kind, kindTitle, onClose, onMarked }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/billing/items", {
          params: { company_id: companyId, kind, invoiced: false },
        });
        setItems(data);
        setSelected(new Set(data.map((it) => it.id))); // pre-select all
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, kind]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(items.map((it) => it.id)));
  const selectNone = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/admin/billing/mark-invoiced", {
        item_ids: Array.from(selected),
      });
      onMarked();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="invoice-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white border-2 border-zinc-900 rounded-md w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">
              Markeer als gefactureerd — {companyName}
            </div>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1">
              {kindTitle} · Nog te factureren
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="invoice-modal-close"
            className="text-zinc-400 hover:text-zinc-900 shrink-0"
            aria-label="Sluiten"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-zinc-500 py-8 text-center">Laden...</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center">
              <CheckCircle size={28} className="mx-auto text-[#43A047]" weight="duotone" />
              <p className="mt-2 text-sm text-zinc-600">Niks meer te factureren voor deze categorie.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs uppercase tracking-widest text-zinc-500 mb-3">
                <span>{selected.size} van {items.length} geselecteerd</span>
                <div className="flex gap-3">
                  <button type="button" onClick={selectAll} className="hover:text-zinc-900" data-testid="invoice-select-all">Alles</button>
                  <button type="button" onClick={selectNone} className="hover:text-zinc-900" data-testid="invoice-select-none">Geen</button>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
                {items.map((it) => (
                  <li key={it.id} className="px-3 py-2.5 flex items-center gap-3" data-testid={`invoice-item-${it.id}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(it.id)}
                      onChange={() => toggle(it.id)}
                      className="h-4 w-4 accent-zinc-900 shrink-0"
                      data-testid={`invoice-item-${it.id}-checkbox`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {it.label}
                        {it.deleted && (
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm">
                            verwijderd
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {KIND_LABEL[it.kind] || it.kind} · Periode gestart{" "}
                        {formatDate(it.period_start)}
                        {it.period_end && ` — ${formatDate(it.period_end)}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {error && (
            <div className="mt-3 border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-3 py-2 rounded-sm flex gap-2 items-start">
              <WarningCircle size={16} weight="duotone" className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="invoice-modal-cancel"
            className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors"
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={selected.size === 0 || submitting || items.length === 0}
            data-testid="invoice-modal-confirm"
            className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#43A047] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <CurrencyEur size={14} weight="bold" />
            {submitting ? "Bezig..." : `Markeer ${selected.size} als gefactureerd`}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
